import React__default, { useState, useEffect } from 'react';
import { a as __assign, b as __spreadArray } from './tslib.es6-18cb2814.js';
import { Divider } from './es/components/Divider.js';
import { Menu } from './es/components/Menu.js';

var isDivider = function (item) {
    return item.kind === "divider";
};
var labelToId = function (label) {
    if (!label) {
        throw new Error("You must provide an ID when using a React Node as a label");
    }
    return label.replace(/[ \\.]/, "");
};
var divider = { kind: "divider" };
var single = function (_a) {
    var label = _a.label, subtext = _a.subtext, checked = _a.checked, id = _a.id, isDisabled = _a.isDisabled, icon = _a.icon;
    return ({
        kind: "single",
        id: id !== null && id !== void 0 ? id : labelToId(typeof label === "string" ? label : ""),
        label: label,
        subtext: subtext,
        checked: checked,
        isDisabled: isDisabled,
        icon: icon,
    });
};
var submenu = function (_a) {
    var label = _a.label, items = _a.items, id = _a.id, subtext = _a.subtext, isDisabled = _a.isDisabled, icon = _a.icon;
    return ({
        kind: "submenu",
        id: id !== null && id !== void 0 ? id : labelToId(typeof label === "string" ? label : ""),
        label: label,
        subtext: subtext,
        items: items,
        isDisabled: isDisabled,
        icon: icon,
    });
};
var canBeChecked = function (item) {
    return item.kind !== "divider";
};
var isChecked = function (item) {
    var _a;
    var getMenuChecked = function (thisItem) {
        var childrenResults = thisItem.items.filter(canBeChecked).map(isChecked);
        if (childrenResults.length === 0) {
            return "indeterminate";
        }
        var allChecked = childrenResults.every(function (r) { return r === true; });
        if (allChecked) {
            return true;
        }
        var allUnchecked = childrenResults.every(function (r) { return r === false; });
        if (allUnchecked) {
            return false;
        }
        return "indeterminate";
    };
    switch (item.kind) {
        case "single":
            return (_a = item.checked) !== null && _a !== void 0 ? _a : false;
        case "submenu":
            return getMenuChecked(item);
        default:
            return false;
    }
};
var getDescendentCheckedCount = function (item) {
    switch (item.kind) {
        case "single":
            return item.checked ? 1 : 0;
        case "submenu":
            return getDescendentsCheckedCount(item.items);
        default:
            return 0;
    }
};
function findSelectedDescendent(items, selectedItem) {
    if (!selectedItem || isDivider(selectedItem)) {
        return false;
    }
    for (var _i = 0, items_1 = items; _i < items_1.length; _i++) {
        var item = items_1[_i];
        if (!isDivider(item) && item.id === selectedItem.id) {
            return !!item;
        }
        else if (item.kind === "submenu" && item.items.length) {
            var result = findSelectedDescendent(item.items, selectedItem);
            // return the result if found, otherwise keep looping
            if (result)
                return !!result;
        }
    }
    return false;
}
var hasSelectedDescendent = function (item, selectedItem) {
    var _a;
    if (!selectedItem || isDivider(item) || item.kind !== "submenu") {
        return false;
    }
    if ((_a = item === null || item === void 0 ? void 0 : item.items) === null || _a === void 0 ? void 0 : _a.length) {
        return findSelectedDescendent(item.items, selectedItem);
    }
    return false;
};
var getDescendentsCheckedCount = function (items) {
    return items.map(getDescendentCheckedCount).reduce(function (a, b) { return a + b; }, 0);
};
var addDescendentCheckedCount = function (item) {
    switch (item.kind) {
        case "divider":
        case "single":
            return item;
        case "submenu":
            return (function () {
                var descendentCheckedCount = getDescendentCheckedCount(item);
                var items = item.items.map(addDescendentCheckedCount);
                return __assign(__assign({}, item), { items: items, descendentCheckedCount: descendentCheckedCount });
            })();
    }
};
var setValue = function (item, toValue) {
    switch (item.kind) {
        case "divider":
            return item;
        case "single":
            return __assign(__assign({}, item), { checked: toValue });
        case "submenu":
            return {
                kind: "submenu",
                id: item.id,
                label: item.label,
                items: item.items.map(function (i) {
                    return i.kind === "divider" ? i : setValue(i, toValue);
                }),
            };
    }
};
var updateItem = function (items, replacement) {
    var updatedItems = items.map(function (item) {
        if (item.kind === "divider") {
            return item;
        }
        else {
            return !isDivider(replacement) && item.id === replacement.id
                ? replacement
                : item;
        }
    });
    return updatedItems;
};
/**
 * Gets an item in the list by its ID. Items in submenus are split using .
 * @param menu
 * @param id Item label without spaces or .
 */
var getById = function (menu, id) {
    var splitAt = id.indexOf(".");
    var searchId = splitAt >= 0 ? id.substring(0, splitAt) : id;
    var matchingItem = menu.find(function (i) {
        return (i.kind === "single" && i.id === searchId) ||
            (i.kind == "submenu" && i.id === searchId);
    });
    if (matchingItem === undefined || matchingItem.kind === "divider") {
        return undefined;
    }
    else if (matchingItem.kind === "single") {
        return matchingItem;
    }
    else {
        var remainingId = splitAt >= 0 ? id.substring(splitAt + 1) : undefined;
        return remainingId
            ? getById(matchingItem.items, remainingId)
            : matchingItem;
    }
};
var getCheckedItems = function (menu) {
    var getCheckedItemsInner = function (currentMenu, parentId, acc) {
        var items = currentMenu.map(function (item) {
            if (item.kind === "divider") {
                return [];
            }
            else if (item.kind === "single") {
                return item.checked ? [parentId + item.id] : [];
            }
            else {
                return getCheckedItemsInner(item.items, "".concat(parentId).concat(item.id, "."), acc);
            }
        });
        return items.reduce(function (acc, n) { return __spreadArray(__spreadArray([], acc, true), n, true); }, acc);
    };
    return new Set(getCheckedItemsInner(menu, "", []));
};

var styles = {"wrapper":"SelectMenu_wrapper_fc586d49","headerWrapper":"SelectMenu_headerWrapper_fc586d49","menuHeader":"SelectMenu_menuHeader_fc586d49","isOpen":"SelectMenu_isOpen_fc586d49","focus-visible":"SelectMenu_focus-visible_fc586d49","selectMenuWrapper":"SelectMenu_selectMenuWrapper_fc586d49","selectItemWrapper":"SelectMenu_selectItemWrapper_fc586d49","hasChildren":"SelectMenu_hasChildren_fc586d49","checked":"SelectMenu_checked_fc586d49","itemWrapper":"SelectMenu_itemWrapper_fc586d49","itemLabel":"SelectMenu_itemLabel_fc586d49","clickable":"SelectMenu_clickable_fc586d49","itemLabelWrapper":"SelectMenu_itemLabelWrapper_fc586d49","labelWithNumber":"SelectMenu_labelWithNumber_fc586d49","label":"SelectMenu_label_fc586d49","bullet":"SelectMenu_bullet_fc586d49","count":"SelectMenu_count_fc586d49","itemCheck":"SelectMenu_itemCheck_fc586d49","divider":"SelectMenu_divider_fc586d49","skeleton":"SelectMenu_skeleton_fc586d49","rotate":"SelectMenu_rotate_fc586d49","dropdownPositionRef":"SelectMenu_dropdownPositionRef_fc586d49"};

var LabelWithNumber = function (props) {
    var label = props.label, n = props.n;
    return (React__default.createElement("div", { className: styles.labelWithNumber },
        React__default.createElement("div", { className: styles.label }, label),
        React__default.createElement("div", { className: styles.bullet }, "\u2022"),
        React__default.createElement("div", { className: styles.count }, n)));
};

var NestedList = function (_a) {
    var items = _a.items, menuId = _a.id, isOpen = _a.isOpen, onUpdate = _a.onUpdate, onItemClick = _a.onItemClick, selectedItem = _a.selectedItem, selectItem = _a.selectItem, mode = _a.mode;
    var _b = useState(undefined), showSubmenuId = _b[0], setShowSubmenuId = _b[1];
    var _c = useState(-1), highlightedIndex = _c[0], setHighlightedIndex = _c[1];
    return (React__default.createElement(Menu.List, { "data-testid": menuId + "-dropdown", onMouseLeave: function () { return setHighlightedIndex(-1); } }, isOpen &&
        items.map(function (i, n) {
            var hasChildren = i.kind === "submenu";
            var isCheckable = mode === "checkbox";
            var submenuOpen = hasChildren && showSubmenuId === i.id;
            return i.kind === "divider" ? (React__default.createElement(Divider, { key: n, className: styles.divider })) : (React__default.createElement(React__default.Fragment, { key: n },
                React__default.createElement(Menu.Item, { key: "".concat(menuId, ".").concat(i.id, "-item"), "data-testid": "".concat(menuId, ".").concat(i.id, "-item"), onMouseOver: function () { return setHighlightedIndex(n); }, onClick: function () {
                        if (hasChildren) {
                            // Toggle the submenu
                            setShowSubmenuId(showSubmenuId === i.id ? undefined : i.id);
                        }
                        else {
                            if (!isCheckable) {
                                selectItem(i);
                            }
                            onItemClick === null || onItemClick === void 0 ? void 0 : onItemClick("".concat(menuId, ".").concat(i.id));
                        }
                    }, hasSubmenu: hasChildren, showSubmenu: submenuOpen, id: "".concat(menuId, ".").concat(i.id), label: i.label, highlighted: highlightedIndex === n, checked: isCheckable && isChecked(i), selected: submenuOpen ||
                        (!!selectedItem &&
                            !isDivider(selectedItem) &&
                            (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.id) === i.id), onCheck: isCheckable
                        ? function (v) { return onUpdate(updateItem(items, setValue(i, v))); }
                        : undefined, count: hasChildren ? i.descendentCheckedCount : 0 }),
                hasChildren && (React__default.createElement(NestedList, { key: "".concat(menuId, ".").concat(i.id), id: "".concat(menuId, ".").concat(i.id), isOpen: showSubmenuId === i.id, items: i.items, onUpdate: function (updated) {
                        return onUpdate(updateItem(items, __assign(__assign({}, i), { items: updated })));
                    }, selectedItem: selectedItem, selectItem: selectItem, onItemClick: onItemClick, mode: mode }))));
        })));
};

var hasDescendentCount = function (item) {
    return "descendentCheckedCount" in item;
};
var getMultiSelectState = function (items) {
    var checkableItems = items
        .map(addDescendentCheckedCount)
        .filter(function (i) { return !isDivider(i); });
    if (checkableItems.every(function (i) {
        return (!isDivider(i) && i.checked) ||
            (hasDescendentCount(i) && !!i.descendentCheckedCount);
    })) {
        return true;
    }
    if (checkableItems.some(function (i) {
        return (!isDivider(i) && i.checked) ||
            (hasDescendentCount(i) && !!i.descendentCheckedCount);
    })) {
        return "indeterminate";
    }
    else {
        return false;
    }
};
var SelectMenuDropdown = function (props) {
    var items = props.items, menuId = props.id, isOpen = props.isOpen, onUpdate = props.onUpdate, onItemClick = props.onItemClick, onClose = props.onClose, mode = props.mode, className = props.className, getMenuProps = props.getMenuProps, getItemProps = props.getItemProps, highlightedIndex = props.highlightedIndex, selectedItem = props.selectedItem, selectItem = props.selectItem;
    var _a = useState(undefined), showSubmenuId = _a[0], setShowSubmenuId = _a[1];
    var _b = useState(getMultiSelectState(items)), headerChecked = _b[0], setHeaderChecked = _b[1];
    var _c = useState(getMultiSelectState(items) === true ? "Clear all" : "Select all"), title = _c[0], setTitle = _c[1];
    var onHeaderCheck = function (checked) {
        onUpdate(items.map(function (i) { return setValue(i, checked); }));
        setHeaderChecked(checked);
    };
    var handleUpdate = function (updatedItems) {
        onUpdate(updatedItems);
        setHeaderChecked(getMultiSelectState(updatedItems));
    };
    useEffect(function () {
        setTitle(headerChecked === true ? "Clear all" : "Select all");
    }, [headerChecked]);
    return (React__default.createElement(Menu, { id: menuId, "data-testid": menuId + "-dropdown", "data-is-open": isOpen, className: styles.selectMenuWrapper, onMouseLeave: function () {
            setShowSubmenuId(undefined);
            onClose();
        }, noPadding: true, header: mode !== "select" && (React__default.createElement(Menu.Title, { title: title, onCheck: onHeaderCheck, checked: headerChecked, count: getDescendentsCheckedCount(items) > 0
                ? getDescendentsCheckedCount(items)
                : undefined })) },
        React__default.createElement(Menu.List, __assign({}, getMenuProps(), { "data-testid": menuId + "-dropdown-list" }), items.map(function (i, n) {
            var isCheckable = mode === "checkbox";
            var hasSubmenu = i.kind === "submenu" && i.items && i.items.length > 0;
            var submenuOpen = hasSubmenu && showSubmenuId === i.id;
            return isDivider(i) ? (React__default.createElement(Divider, __assign({ key: n, className: styles.divider }, getItemProps({
                item: i,
                index: n,
                disabled: true,
                role: "separator",
            })))) : (React__default.createElement(React__default.Fragment, { key: n },
                React__default.createElement(Menu.Item, __assign({ id: "".concat(menuId, ".").concat(i.id), key: "".concat(menuId, ".").concat(i.id), "data-testid": "".concat(menuId, ".").concat(i.id, "-item"), label: i.label, checked: isCheckable && isChecked(i), onCheck: isCheckable
                        ? function (v) { return handleUpdate(updateItem(items, setValue(i, v))); }
                        : undefined, count: hasDescendentCount(i) && i.descendentCheckedCount
                        ? i.descendentCheckedCount
                        : 0, hasSelectedDescendent: hasSelectedDescendent(i, selectedItem), hasSubmenu: hasSubmenu, showSubmenu: submenuOpen, highlighted: highlightedIndex === n && !submenuOpen, selected: selectedItem &&
                        !isDivider(selectedItem) &&
                        (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.id) === i.id }, getItemProps({ item: i, index: n }), { onClick: function () {
                        if (hasSubmenu) {
                            setShowSubmenuId(showSubmenuId === i.id ? undefined : i.id);
                        }
                        else {
                            if (!isCheckable) {
                                selectItem(i);
                            }
                            onItemClick === null || onItemClick === void 0 ? void 0 : onItemClick("".concat(menuId, ".").concat(i.id));
                        }
                    } })),
                hasSubmenu && (React__default.createElement(NestedList, { key: "".concat(menuId, ".").concat(i.id, "-submenu"), id: "".concat(menuId, ".").concat(i.id), isOpen: showSubmenuId === i.id, items: i.items, onUpdate: function (updated) {
                        return onUpdate(updateItem(items, __assign(__assign({}, i), { items: updated })));
                    }, onItemClick: onItemClick, selectedItem: selectedItem, selectItem: selectItem, mode: mode, className: className }))));
        }))));
};

export { LabelWithNumber as L, SelectMenuDropdown as S, getCheckedItems as a, single as b, submenu as c, divider as d, addDescendentCheckedCount as e, getDescendentsCheckedCount as f, getById as g, styles as h, isChecked as i, setValue as s, updateItem as u };
